import { BaseModal, FormActions, FormField, FormInput, FormSelect } from '@practium/ui';
import { usePlanForm } from '../../hooks/usePlanForm';
import type { PlanGeneralForm } from '../../schemas/parameters.schema';
import type { FertilizationPlanReferenceDTO } from '../../types/parameters.types';
import { ValueInput } from '../Forms/ValueInput';
import styles from '../Sections/Sections.module.css';

interface PlanFormModalProps {
	plans: FertilizationPlanReferenceDTO[];
	plan: FertilizationPlanReferenceDTO | null;
	isSaving: boolean;
	onSubmit: (form: PlanGeneralForm, base: FertilizationPlanReferenceDTO) => Promise<boolean>;
	onActivate?: () => void;
	onDelete?: () => void;
	onClose: () => void;
}

export const PlanFormModal = ({ plans, plan, isSaving, onSubmit, onActivate, onDelete, onClose }: PlanFormModalProps) => {
	const { form, baseId, activePlan, errors, setField, setBaseId, submit } = usePlanForm({
		plans,
		plan,
		onSubmit,
		onClose,
	});
	const isEdit = !!plan;
	const baseOptions = plans.map((item) => ({ value: item._id, label: `${item.name} (versão ${item.version})` }));

	return (
		<BaseModal
			isOpen
			maxWidth="48rem"
			title={isEdit ? 'Editar plano de cálculo' : 'Novo plano de cálculo'}
			isLoading={isSaving}
			onClose={onClose}
			actions={
				<FormActions
					onCancel={isEdit ? undefined : onClose}
					onDelete={onDelete}
					deleteInActions={!!onDelete}
					onSecondary={onActivate}
					secondaryText="Ativar plano"
					secondaryDisabled={isSaving}
					onSave={submit}
					isLoading={isSaving}
					saveText={isEdit ? 'Salvar' : 'Criar e ativar'}
				/>
			}
		>
			<div className={styles.form}>
				{!isEdit && activePlan && (
					<div className={styles.notice}>
						<span>
							O plano "{activePlan.name}" será desativado, e o novo plano passará a ser usado no cálculo de fertilização.
						</span>
					</div>
				)}
				<div className={styles.fields}>
					<FormField label="Nome" required error={errors.name}>
						<FormInput
							value={form.name}
							hasError={!!errors.name}
							onChange={(event) => setField('name', event.target.value)}
						/>
					</FormField>
					<FormField label="Versão" required error={errors.version}>
						<ValueInput
							label="Versão"
							min={1}
							value={form.version}
							hasError={!!errors.version}
							onChange={(value) => setField('version', value as number)}
						/>
					</FormField>
					<div className={styles.fieldsFull}>
						<FormField label="Descrição">
							<FormInput value={form.description} onChange={(event) => setField('description', event.target.value)} />
						</FormField>
					</div>
					{!isEdit && (
						<div className={styles.fieldsFull}>
							<FormField label="Copiar coeficientes de" required>
								<FormSelect value={baseId} options={baseOptions} onChange={setBaseId} />
							</FormField>
						</div>
					)}
				</div>
			</div>
		</BaseModal>
	);
};
